const React = require("react");
const { createCJDropshippingApi } = require("../api/cjDropshipping.api");
const { confirmCJPassword, getActiveCJConfirmationToken, clearCJConfirmation } = require("../../../src/includes/pageInside/sync/CJPasswordConfirmationSession");
// `Family()` est le vrai check de rôle utilisé partout ailleurs dans Elakay
// (menuUser.jsx, Footer.js, userAccount.js, 30+ fichiers) -- lit directement
// isAuthenticated() en interne, pas besoin de lui passer roleName/infoSource.
// Module dans front_elakay/src/, ce module CJ vit dans
// front_elakay/dropShipping/CJDropshipping/ mais est bundlé par le même
// build CRA -- import relatif, même précédent que allPriceFeet.jsx.
const { Family } = require("../../../src/includes/pageInside/Rolle.js");

const CJDropshippingContext = React.createContext(null);

const FAMILY_ROLES = new Set(["family", "familly", "famille"]);

const CJDropshippingProvider = ({
  baseUrl, token, userId = "", translate, roleName = "", visiblePlatformIds = null,
  // `contentTopRef` : ancre posée juste sous le Header Elakay, au tout début
  // du contenu rendu par CJDropshippingDashboard.jsx (voir ce fichier) --
  // prop-drillée depuis là jusqu'ici. Permet aux pages du module (ex:
  // pagination dans ProductCollectionPage.js) de revenir en scroll fluide
  // au début du contenu sans remonter tout en haut du navigateur, ce qui
  // masquerait le résultat sous le Header quand il est fixed/sticky.
  contentTopRef = null,
  children,
}) => {
  const api = React.useMemo(
    () => createCJDropshippingApi({
      baseUrl,
      getToken: () => token,
      confirmPassword: confirmCJPassword,
      getConfirmationToken: getActiveCJConfirmationToken,
      onConfirmationInvalid: clearCJConfirmation,
    }),
    [baseUrl, token]
  );
  const t = React.useCallback(
    (key, fallback, values) => translate ? translate(key, fallback, values) : fallback,
    [translate]
  );
  // Family() (hash SHA-512 dérivé de isAuthenticated(), le check "officiel"
  // utilisé dans tout Elakay) fait autorité ; le roleName déclaré reste un
  // fallback pour ne rien casser si Family() ne matche pas (comptes migrés
  // vers le nouveau système de rôles sans le hash legacy, ou tests).
  const isFamily = Boolean(Family()) || FAMILY_ROLES.has(String(roleName).trim().toLowerCase());
  // Compatibilité interne : les anciens composants qui consultent encore
  // `isAdmin` ne doivent jamais élargir l'accès au-delà de Family.
  const isAdmin = isFamily;
  const visiblePlatformSet = React.useMemo(
    () => Array.isArray(visiblePlatformIds) ? new Set(visiblePlatformIds) : null,
    [visiblePlatformIds]
  );
  const isPlatformVisible = React.useCallback(
    (platformId) => !visiblePlatformSet || visiblePlatformSet.has(platformId),
    [visiblePlatformSet]
  );

  // CJ connection status drives the "Signup" nav/page: once a seller has an
  // active CJ connection, that onboarding flow no longer applies (see
  // DropshippingLayout.js / navigation.js). Fetched once here so it's
  // available to the layout without every page re-fetching it itself.
  const [cjAccountStatus, setCJAccountStatus] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    api.getAccountStatus()
      .then((data) => { if (!cancelled) setCJAccountStatus(data.accountStatus || null); })
      .catch(() => { if (!cancelled) setCJAccountStatus(null); });
    return () => { cancelled = true; };
  }, [api]);
  const isCJConnected = cjAccountStatus?.isConnected === true;

  const value = React.useMemo(
    () => ({ api, t, userId, isAdmin, isFamily, isPlatformVisible, cjAccountStatus, isCJConnected, contentTopRef }),
    [api, t, userId, isAdmin, isFamily, isPlatformVisible, cjAccountStatus, isCJConnected, contentTopRef]
  );
  return React.createElement(CJDropshippingContext.Provider, { value }, children);
};

const useCJDropshipping = () => {
  const value = React.useContext(CJDropshippingContext);
  if (!value) throw new Error("CJDropshippingProvider is missing");
  return value;
};

module.exports = { CJDropshippingProvider, useCJDropshipping };
